import { googleFontVariations } from '@apps/guest/packages/layout-engine/common/fonts';
import { layoutConfigs } from '@apps/guest/packages/layout-engine/layouts/layout.constants';
import { EventDesignFragment } from '@graphql/generated';
import { Theme } from '@withjoy/joykit';
import globalWindow from '@shared/core/globals';
import { useEffect, useState } from 'react';
import { withWindow } from '@shared/utils/withWindow';
import { getURLParams } from '@shared/utils/urlHelpers';
import { SearchParams } from './GuestSite.types';
import { useFeatureFlagsContext } from '@shared/core/featureFlags';
import { GuestSiteEvent } from '@apps/guest/packages/layout-engine/layouts/layout.types';
import { isInIframe } from '@shared/utils/isInIframe';
import useSet from '@shared/utils/hooks/useSet';

type UseEventDesignThemingOptions = Readonly<{
  eventDesign: Maybe<EventDesignFragment>;
}>;

export const useEventDesignTheming = ({ eventDesign }: UseEventDesignThemingOptions) => {
  const layoutConfig = eventDesign?.websiteLayout.layoutType === 'brannan' ? layoutConfigs.brannan : layoutConfigs.aloha;
  const [theme, setTheme] = useState<Theme>(eventDesign ? layoutConfig.configureThemeOverrides(eventDesign, layoutConfig.theme) : layoutConfig.theme);
  const [_, { addMultiple: addFonts, has: hasFont }] = useSet();
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (layoutConfig) {
      // Collect fonts to load
      const layoutFontFamilies = layoutConfig.fonts.google?.map(key => googleFontVariations[key]) || [];
      const eventDesignFontFamilies = [eventDesign?.font.fontFamily];

      // Update JoyKit with `eventDesign` applied to a layout, otherwise use the default layout theme
      const updateTheme = () => {
        setTheme(eventDesign ? layoutConfig.configureThemeOverrides(eventDesign, layoutConfig.theme) : layoutConfig.theme);
      };
      const arrFonts = [...layoutFontFamilies, ...eventDesignFontFamilies].filter(x => x) as string[];
      if (globalWindow.WebFont) {
        const getFontsUnloaded = arrFonts.filter((font: string) => !hasFont(font));
        if (!getFontsUnloaded.length) {
          updateTheme();
          return;
        }

        setLoading(true);
        // 1. Attempt to load fonts first to prevent FOUT
        // 2. Update theme regardless of font load success
        globalWindow.WebFont?.load({
          google: {
            families: getFontsUnloaded
          },
          // Either the `active` or `inactive` callback will be invoked
          active: () => {
            addFonts(getFontsUnloaded);
            setLoading(false);
            updateTheme();
          },

          inactive: () => {
            setLoading(false);
            updateTheme();
          }
        });
      } else {
        updateTheme();
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventDesign, layoutConfig, setTheme]);

  return { theme, loadingFonts: loading };
};

export const shouldUseEventDesignDraft = () =>
  withWindow<boolean>(() => {
    const params = getURLParams<SearchParams>(window.location.search);
    // only use event design draft if:
    // - rendering for a preview
    // - the preview is for the website designer
    // effectively only request the event design draft if you are on the website designer
    return params.preview === 'true' && params.ctx === 'websiteDesigner' ? true : false;
  }, false);

export const useReloadForLegacyGuestsite = (eventFeatureFlags: GuestSiteEvent['featureFlags'], website: Maybe<string>) => {
  const featureFlagsContext = useFeatureFlagsContext();

  //If the event does not have the 'useCharm' feature flag
  // check to see if there's a param flag in place
  // if is NOT charm and NONE of the param flags are set properly
  // reload, which will redirect away
  useEffect(() => {
    if (eventFeatureFlags && !eventFeatureFlags.includes('useCharm')) {
      withWindow(window => {
        const params = getURLParams<SearchParams>(window.location.search);
        if (
          params.ctx === 'adminGuestSitePreview' ||
          params.preview === 'true' ||
          params.target === 'charm' ||
          featureFlagsContext.getFeatureFlagValue('nossr') ||
          // assume that if you are in an iframe you should be rendering in a preview
          // state and you should therefore always render the guest app instead of app-browser
          isInIframe() ||
          featureFlagsContext.getFeatureFlagValue('guestsitetarget') === 'charm' ||
          // special events passing through to charm guest site
          website === 'richardtait'
        ) {
          return;
        } else {
          // render the app-browser site by reloading the page allowing charm server to kick the user to nginx
          window.location.reload();
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featureFlagsContext, eventFeatureFlags]);
};
